import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";

export default function Footer() {
  const { t, loc } = useI18n();
  const { content } = useSiteData();
  const offices = content.contact?.offices || [];
  const f = content.footer || {};
  const txt = (k, fb) => loc(f[k]) || t(fb);
  const nav = (content.menu?.items || []).filter((i) => i && i.visible !== false);

  return (
    <footer className="bg-[#EFECE6] border-t border-[#E2E0D8]">
      <div className="shell py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="mono text-[9.5px] text-[#6B675F] mb-6">{txt("label", "footer.getInTouch")}</p>
            <h3 className="display text-[38px] sm:text-[52px] lg:text-[58px] text-[#141414] max-w-[520px] whitespace-pre-line leading-[1.0]">
              {txt("title", "footer.title")}
            </h3>
            <Link
              to="/contact"
              className="mt-9 inline-flex items-center gap-2 border border-[#141414] px-6 py-3.5 mono text-[10px] text-[#141414] hover:bg-[#141414] hover:text-[#F7F6F2] transition-colors duration-300"
            >
              {txt("ctaLabel", "cta.startProject")} <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <p className="mono text-[9.5px] text-[#6B675F] mb-6">{txt("navLabel", "footer.navigate")}</p>
            <ul className="space-y-3">
              {nav.map((n) => (
                <li key={n.path}>
                  <Link
                    to={n.path}
                    className="text-[15px] text-[#141414]/75 hover:text-[#141414] link-underline transition-colors duration-300"
                  >
                    {loc(n.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {offices.map((o) => (
            <div key={o.country} className="lg:col-span-2">
              <p className="mono text-[9.5px] text-[#6B675F] mb-6">{o.country}</p>
              <p className="text-[14px] text-[#141414] leading-relaxed mb-2">{o.name}</p>
              <p className="text-[13.5px] text-[#141414]/60 leading-relaxed">
                {loc(o.city) || o.address.join(", ")}
              </p>
              <div className="mt-4 space-y-1">
                {o.phones.map((p) => (
                  <a
                    key={p}
                    href={`tel:${p.replace(/\s/g, "")}`}
                    className="flex items-center gap-2 text-[13.5px] text-[#141414]/75 hover:text-[#141414] transition-colors"
                  >
                    <span>{p}</span>
                    {o.phoneNote && <span className="mono text-[8px] text-[#8C7862]">({o.phoneNote})</span>}
                  </a>
                ))}
                <a href={`mailto:${o.email}`} className="block text-[13.5px] text-[#141414] link-underline">
                  {o.email}
                </a>
                <a
                  href={o.website}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-[13.5px] text-[#141414]/75 hover:text-[#141414] link-underline"
                >
                  {o.websiteLabel}
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 pt-7 border-t border-[#E2E0D8] flex flex-col sm:flex-row gap-4 justify-between">
          <p className="mono text-[9px] text-[#6B675F]">
            &copy; {new Date().getFullYear()} HRD Architecture · {txt("rights", "footer.rights")}
          </p>
          <p className="mono text-[9px] text-[#6B675F] max-w-[420px] sm:text-right">{loc(f.note) || loc(content.about?.philosophy)}</p>
        </div>
      </div>
    </footer>
  );
}
